// 附近場站列表
import React, { useRef, useState } from 'react';
import { LoadingOverlay, Text, Title } from '@mantine/core';
import LocationCard from './LocationCard.tsx';
import { flushSync } from 'react-dom';
import { useTheme } from '../../../context/ThemeProvider.tsx';
import { PlaceWithServices } from '../../../models/Place.ts';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useMap } from '@vis.gl/react-google-maps';

interface NearbyPlacesListProps {
  places: PlaceWithServices[];
  isPlacesLoading: boolean;
  selectedLocation: PlaceWithServices;
  setSelectedLocation: (selectedLocation: PlaceWithServices) => void;
  isNearbyPlacesListOpen: boolean;
  defaultCenterSelectedZoom: number;
  setHasUserMovedMap: (hasUserMoved: boolean) => void;
}

const NearbyPlacesList: React.FC<NearbyPlacesListProps> = ({
  places,
  isPlacesLoading,
  selectedLocation,
  setSelectedLocation,
  isNearbyPlacesListOpen,
  defaultCenterSelectedZoom,
  setHasUserMovedMap,
}) => {
  const map = useMap();
  const [showTopShadow, setShowTopShadow] = useState(false); // 處理 scroll area 的陰影(top)
  const [showBottomShadow, setShowBottomShadow] = useState(false); // 處理 scroll area 的陰影(bottom)
  const scrollRef = useRef<HTMLDivElement>(null);
  const { colorScheme } = useTheme();

  // 處理 scroll area 的陰影
  const handleScroll = () => {
    if (!scrollRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

    setShowTopShadow(scrollTop > 0);
    setShowBottomShadow(scrollTop + clientHeight < scrollHeight - 1);
  };

  const handleLocationCardClick = (place: PlaceWithServices) => {
    flushSync(() => setSelectedLocation(null));
    flushSync(() => setSelectedLocation(place));

    // 設定地圖中心點
    map.setCenter({
      lat: place.location.lat,
      lng: place.location.lng,
    });
    map.setZoom(defaultCenterSelectedZoom);
    // 資訊卡牌再選定地點時預設都會展開, 故 要把地圖中心稍微移動, 以避免被遮蓋住
    map.panBy(-170, 0);
    // 如果附近場站卡牌也有展開, 就需要另外再 panBy -200
    if (isNearbyPlacesListOpen) map.panBy(-200, 0);

    setHasUserMovedMap(false);
  };

  const getEstimatedLocationCardHeight = (place: PlaceWithServices) => {
    const count = place.services.length;

    if (count === 0) return 95;
    if (count === 1) return 111;
    return 111 + 24 * (count - 1);
  };

  // 當有選定地點時, 為附近場站列表創建 virtualizer
  const otherPlacesVirtualizer = useVirtualizer({
    count: selectedLocation
      ? places.filter((place) => place.id !== selectedLocation.id).length
      : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      const place = places[index];
      return getEstimatedLocationCardHeight(place);
    },
    overscan: 5,
  });

  // 當沒有選定地點時, 為所有地點列表創建 virtualizer
  const allPlacesVirtualizer = useVirtualizer({
    count: !selectedLocation ? places.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      const place = places[index];
      return getEstimatedLocationCardHeight(place);
    },
    overscan: 5,
  });

  // 取得 選定以外的所有地點
  const otherPlaces = selectedLocation
    ? places.filter((place) => place.id !== selectedLocation.id)
    : [];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 20,
        marginLeft: 8,
        width: 360,
        position: 'absolute',
        top: 0,
        bottom: '20px',
        left: '30px',
        border: '2px solid',
        borderColor: 'rgba(205,205,205,0.37)',
        overflow: 'hidden',
        backgroundColor: colorScheme === 'light' ? 'white' : 'black',
      }}
    >
      {/* 載入時顯示 LoadingOverlay */}
      <LoadingOverlay
        visible={places && places.length > 0 && isPlacesLoading}
        overlayProps={{ radius: 'sm', blur: 1 }}
        loaderProps={{
          color: 'rgb(67,154,255)',
          type: 'bars',
          size: 'sm',
        }}
      />

      {/* 標題: 圖資總筆數 */}
      <div
        style={{
          width: 356,
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <Title order={6} size={14} c="dimmed" m={'sm'} fw={400}>
          圖資總筆數: {places.length}
        </Title>
      </div>

      {/* 內容: 圖資卡牌 — flex: 1 填滿標題以下的剩餘空間 */}
      <div
        style={{
          width: 356,
          position: 'relative',
          overflow: 'hidden',
          flex: 1,
        }}
      >
        {/* 有選定地點時的格式 */}
        {selectedLocation && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflowX: 'hidden',
              padding: '0px',
              margin: '0 10px 10px 10px',
            }}
          >
            {/* 選定地點區塊 */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                marginBottom: 8,
                flexShrink: 0,
              }}
            >
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  color: 'rgb(134, 142, 150)',
                  paddingLeft: 2,
                }}
              >
                選定地點
              </Text>
              <LocationCard
                place={selectedLocation}
                isSelected={true}
                onClick={() => {
                  handleLocationCardClick(selectedLocation);
                }}
              />
            </div>

            {/* 其他地點區塊：使用 virtual */}
            <Text
              style={{
                fontSize: 12,
                fontWeight: 400,
                color: 'rgb(134, 142, 150)',
                paddingLeft: 2,
                paddingBottom: 4,
                flexShrink: 0,
              }}
            >
              其他地點
            </Text>

            {/* 陰影 + 滾動區 wrapper */}
            <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
              {showTopShadow && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '15px',
                    background:
                      'linear-gradient(to bottom, rgba(0, 0, 0, 0.1), transparent)',
                    pointerEvents: 'none',
                    zIndex: 10,
                  }}
                />
              )}
              {showBottomShadow && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '15px',
                    borderRadius: '0px 0px 4px 4px',
                    background:
                      'linear-gradient(to top, rgba(0, 0, 0, 0.1), transparent)',
                    pointerEvents: 'none',
                    zIndex: 10,
                  }}
                />
              )}
              <div
                ref={scrollRef}
                onScroll={handleScroll}
                style={{
                  height: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }}
              >
                <div
                  style={{
                    height: otherPlacesVirtualizer.getTotalSize(),
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {otherPlacesVirtualizer
                    .getVirtualItems()
                    .map((virtualItem) => {
                      const place = otherPlaces[virtualItem.index];
                      return (
                        <div
                          key={virtualItem.key}
                          data-index={virtualItem.index}
                          ref={otherPlacesVirtualizer.measureElement}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            transform: `translateY(${virtualItem.start}px)`,
                            paddingBottom: 8,
                          }}
                        >
                          <LocationCard
                            place={place}
                            isSelected={false}
                            onClick={() => {
                              handleLocationCardClick(place);
                            }}
                          />
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 沒有選定地點時的格式 */}
        {!selectedLocation && (
          /* 陰影 + 滾動區 wrapper */
          <div style={{ height: '100%', position: 'relative' }}>
            {showTopShadow && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '15px',
                  background:
                    'linear-gradient(to bottom, rgba(0, 0, 0, 0.1), transparent)',
                  pointerEvents: 'none',
                  zIndex: 10,
                }}
              />
            )}
            {showBottomShadow && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '15px',
                  borderRadius: '0px 0px 4px 4px',
                  background:
                    'linear-gradient(to top, rgba(0, 0, 0, 0.1), transparent)',
                  pointerEvents: 'none',
                  zIndex: 10,
                }}
              />
            )}
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              style={{
                marginLeft: 10,
                height: '100%',
                overflowX: 'hidden',
                overflowY: 'auto',
                padding: '0px',
              }}
            >
              {/* 附近所有地點：都使用 virtual */}
              <div
                style={{
                  height: allPlacesVirtualizer.getTotalSize(),
                  width: '100%',
                  position: 'relative',
                }}
              >
                {allPlacesVirtualizer.getVirtualItems().map((virtualItem) => {
                  const place = places[virtualItem.index];
                  return (
                    <div
                      key={virtualItem.key}
                      data-index={virtualItem.index}
                      ref={allPlacesVirtualizer.measureElement}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualItem.start}px)`,
                        marginBottom: 8,
                        paddingBottom: 8,
                      }}
                    >
                      <LocationCard
                        place={place}
                        isSelected={false}
                        onClick={() => handleLocationCardClick(place)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NearbyPlacesList;
